import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Image,
  StyleSheet,
  ScrollView,
  Text,
  View,
} from './components/base_components';
import { useBusy, BusyOverlay } from './components/busy_component';
import LoginBoxes from './components/login_boxes';
import InlineTextButton from './components/buttons/inline_text_button';
import ProgressSpinner from './components/progress_spinner';
import Plan from './components/plan';
import PlanList from './components/plan_list';
import CreditCard from './components/credit_card';
import SignupDialog from './components/signup_dialog';

import PlanStore from './stores/plan_store';
import SignupStore from './stores/signup_store';
import UserStore from './stores/user_store';
import VolumeStore from './stores/volume_store';

import util from './tools/util';

const styles = StyleSheet.create({
  signupScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'var(--signup-bg-color)',
  },
  scroll: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'column',
  },
  scrollInner: {
    minHeight: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  planView: {
    marginTop: '1.5rem',
    alignSelf: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  subtitle: {
    marginTop: '1.5rem',
    color: 'var(--signup-subtitle-text)',
    fontSize: '1.8rem',
    fontWeight: '500',
    textAlign: 'center',
  },
  card: { marginTop: '2rem' },
  logo: {
    marginTop: '3rem',
    marginBottom: '3rem',
    width: '12rem',
    height: '2.4rem',
  },
  title: {
    color: 'var(--signup-title-text)',
    fontSize: '3rem',
    fontWeight: '600',
    textAlign: 'center',
  },
  padBottom: { height: '5rem' },
  link: {
    marginRight: '1.5rem',
    order: 99,
    alignSelf: 'end',
    color: 'var(--link-text-color)',
    fontSize: '1.3rem',
    fontWeight: 600,
    textDecoration: 'underline',
  },
});

const LOGO = require('./assets/img/logo.png');
const BANK = require('./assets/img/bank_black.svg');
const ORG = require('./assets/img/org_black.svg');
const ROCKET = require('./assets/img/rocket_black.svg');

export default function SignupScreen() {
  const user = UserStore.useUser();
  const is_ready = SignupStore.useIsReady();
  const org = SignupStore.useOrg();
  const has_volume = SignupStore.useHasVolume();
  const has_payment = SignupStore.useHasPayment();
  const plan = SignupStore.usePlan();
  const [org_name, setOrgName] = React.useState('');
  const [volume_name, setVolumeName] = React.useState('');
  const [is_busy, setBusy, clearBusy] = useBusy();
  const navigate = useNavigate();
  const custom_domain = UserStore.getCustomDomain();

  React.useEffect(() => {
    PlanStore.fetch();
    SignupStore.init();
  }, []);

  React.useEffect(() => {
    if (custom_domain) {
      util.log('SignupScreen: no signup from a custom URL.');
      navigate('/');
    }
  }, [custom_domain, navigate]);

  function _onLoginPress() {
    navigate('/login');
  }
  function _onLoginSuccess() {}
  function _onCreateOrgPress() {
    if (org_name && setBusy()) {
      SignupStore.createOrg(org_name, (err, org_id) => {
        if (err === 409) {
          window.alert('Organization name already in use.');
        } else if (err) {
          window.alert('Create failed, please try again.');
        }
        clearBusy();
      });
    }
  }
  function _onCreateVolumePress() {
    if (volume_name && setBusy()) {
      const opts = { org_id: org.org_id, volume_name };
      VolumeStore.createVolume(opts, (err) => {
        clearBusy();
        if (err === 400) {
          window.alert(
            'Sorry, your volume name is illegal.  The name must between 3 and 64 characters, must start and end with a letter or number, and may only contain the characters A-Z, a-z, 0-9 and space, underscore, and dash.'
          );
        } else if (err) {
          window.alert('Create failed, please try again.');
        } else {
          _onFinishSignup();
        }
      });
    }
  }
  function _onPlanPress(new_plan) {
    SignupStore.savePlan(new_plan);
    if (has_payment) {
      _onSavePlanPress();
    }
  }
  function _onChangePlanPress() {
    SignupStore.savePlan(null);
  }
  function _onSavePlanPress() {
    if (setBusy()) {
      SignupStore.updatePlan((err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to save plan, please try again.');
        }
      });
    }
  }
  function _onFinishSignup() {
    if (setBusy()) {
      SignupStore.finish(() => {
        clearBusy();
        navigate('/');
      });
    }
  }

  let content: any = null;
  if (!is_ready) {
    content = <ProgressSpinner />;
  } else if (!user) {
    content = (
      <LoginBoxes
        title='Get Started with Us!'
        subtitle='Enter your work email address.'
        buttonText='Signup'
        bottomText='Already have an account?'
        bottomLinkText='Log In'
        onBottomPress={_onLoginPress}
        onSuccess={_onLoginSuccess}
      />
    );
  } else if (user && !org) {
    content = (
      <SignupDialog
        title='Name your Organization'
        subtitle='Usually your company name.'
        placeholder='New Organization Name'
        icon={ORG}
        value={org_name}
        onChangeText={setOrgName}
        buttonText='Next'
        onPress={_onCreateOrgPress}
      />
    );
  } else if (!plan) {
    content = (
      <>
        <Image style={styles.logo} source={LOGO} />
        <Text style={styles.title}>Choose Plan</Text>
        <PlanList onPress={_onPlanPress} />
        <View style={styles.padBottom} />
      </>
    );
  } else if (!has_payment) {
    content = (
      <SignupDialog title='Confirm your Plan' subtitle='' icon={BANK}>
        <View style={styles.planView}>
          <Plan plan={plan} hideChecks={true} />
          <InlineTextButton
            style={styles.link}
            text='Change'
            onPress={_onChangePlanPress}
          />
        </View>
        <Text style={styles.subtitle}>Enter your payment details</Text>
        <CreditCard
          style={styles.card}
          orgId={org.org_id}
          onSuccess={_onSavePlanPress}
        />
      </SignupDialog>
    );
  } else if (!org.plan_id) {
    content = (
      <SignupDialog
        title='Save Plan'
        subtitle=''
        buttonText='Next'
        icon={BANK}
        onPress={_onSavePlanPress}
      />
    );
  } else if (!has_volume) {
    content = (
      <SignupDialog
        title='Name your First Volume'
        subtitle='Maxlo arranges your data in volumes you can share with other users and devices.'
        placeholder='New Volume Name'
        value={volume_name}
        icon={ORG}
        onChangeText={setVolumeName}
        buttonText='Next'
        onPress={_onCreateVolumePress}
      />
    );
  } else {
    content = (
      <SignupDialog
        title='Finish Signup'
        subtitle=''
        buttonText='Next'
        icon={ROCKET}
        onPress={_onFinishSignup}
      />
    );
  }

  return (
    <View style={styles.signupScreen}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollInner}
      >
        {content}
      </ScrollView>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
